import { useUnifiedNav } from "@features/unified-side-nav-feature/context/UnifiedNavContext";
import { ImpersonationSelection } from "../../types";
import { Box, Button, Typography } from "@mui/material";
import ChevronDivider from "@features/impersonation-feature/components/Panel/ChevronDivider";
import SyncAltIcon from "@mui/icons-material/SyncAlt";
import { telemetryProps } from "shared-ui/src/instrumentation/gtm";
import {
  handleImpersonateAccountWithoutConfirmation,
  handleImpersonateUser,
} from "functions/handleImpersonateAccount";
import AccountSelector from "./AccountSelector";
import UserSelector from "./UserSelector";
import { isDemoAccountByAccountId } from "shared-ui/src/utils/demoDataHelpers";
import { isSamePersona } from "../../personaIdentity";
import {
  isSameAccountSelection,
  isSameOriginAccount,
} from "../../selectionIdentity";
import TenantSelector from "./TenantSelector";
import { TENANT_ID_UNDEFINED } from "../../constants";
import { isOnboardedTenant } from "@features/impersonation-feature/hooks/useGetTenant";

export function ImpersonationPanel({
  origin = null,
}: {
  origin?: ImpersonationSelection | null;
}) {
  const {
    impersonationState: { current, pending },
    setImpersonationState,
  } = useUnifiedNav();

  // The "working" selection: prefer pending when it exists, fall back to current
  const working: ImpersonationSelection | null = pending ?? current;
  const workingAccount = working?.account ?? null;

  const hasPending = pending !== null;

  const hasPendingAccountChange =
    hasPending && !isSameAccountSelection(pending, current);

  const hasPendingUserChange =
    hasPending && !isSamePersona(pending.user, current?.user ?? null);

  const isOriginPreviewSelection =
    working?.tenant?.id === TENANT_ID_UNDEFINED &&
    !isOnboardedTenant(origin?.tenant?.onBoardingStatus ?? null) &&
    (working?.account?.id ?? null) === (origin?.account?.id ?? null);

  const isWorkingOrigin =
    isOriginPreviewSelection ||
    (isSameOriginAccount(working, origin) &&
      isSamePersona(working?.user ?? null, origin?.user ?? null));

  const isPreviewMode =
    working?.tenant?.id === TENANT_ID_UNDEFINED ||
    !isOnboardedTenant(working?.tenant?.onBoardingStatus ?? null);

  const shouldShowSwitchButton =
    hasPending &&
    !isWorkingOrigin &&
    ((hasPendingUserChange &&
      pending.user?.id != null &&
      pending.user.type != null) ||
      (hasPendingAccountChange && pending.account != null));

  const shouldShowSwitchLabel =
    shouldShowSwitchButton || (isPreviewMode && workingAccount == null);

  const applySwitch = () => {
    if (!pending) return;

    const pendingAccount = pending.account ?? null;

    if (isPreviewMode && hasPendingAccountChange) {
      if (pendingAccount?.id == null) return;
      handleImpersonateAccountWithoutConfirmation(null, pendingAccount.id);
    } else if (hasPendingUserChange) {
      if (pending.user?.id == null) return;

      const id = pending.user.id;

      if (pending.user.type === "account") {
        if (id !== pendingAccount?.id) return;
        handleImpersonateAccountWithoutConfirmation(null, pendingAccount.id);
        return;
      }

      if (pending.user.type !== "guid") return;

      const isDemoAccount = isDemoAccountByAccountId(workingAccount?.id ?? 0);

      handleImpersonateUser(null, String(id), isDemoAccount);
    } else {
      setImpersonationState((prev) => ({
        ...prev,
        current: pending,
        pending: null,
      }));
    }
  };

  return (
    /* ── Center: label + sections + Switch — always centered ── */
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: { xs: 0.75, sm: 1 },
        flexWrap: { xs: "wrap", sm: "nowrap" },
        rowGap: { xs: 0.75, sm: 1 },
        width: "100%",
        justifyContent: { xs: "center", md: "flex-start" },
        minWidth: 0,
      }}
    >
      {/* Label */}
      <Typography
        variant="caption"
        sx={{
          color: "text.secondary",
          whiteSpace: "nowrap",
          letterSpacing: (theme) => theme.typography.caption.letterSpacing,
          mr: 0.5,
          flexShrink: 0,
          fontWeight: "fontWeightMedium",
          flexBasis: { xs: "100%", sm: "auto" },
          textAlign: { xs: "center", sm: "left" },
        }}
      >
        {shouldShowSwitchLabel ? "Switch to" : "You are viewing"}
      </Typography>

      {/* ── 1. Tenant section ── */}
      <TenantSelector
        pending={pending}
        working={working}
        origin={origin}
        setImpersonationState={setImpersonationState}
      />

      <Box sx={{ display: { xs: "none", sm: "block" } }}>
        <ChevronDivider />
      </Box>

      {/* ── 3. Customer Account picker (preview mode) OR User picker (normal mode) ── */}
      {isPreviewMode ? (
        <AccountSelector
          pending={pending}
          working={working}
          origin={origin}
          setImpersonationState={setImpersonationState}
        />
      ) : (
        <UserSelector
          pending={pending}
          working={working}
          setImpersonationState={setImpersonationState}
        />
      )}

      {/* ── Switch button — inline with controls ── */}
      {shouldShowSwitchButton ? (
        <Button
          {...telemetryProps({
            name: "impersonation_switch",
            section: "impersonation_panel",
            label: "Switch",
          })}
          variant="contained"
          color="primary"
          size="small"
          startIcon={<SyncAltIcon fontSize="small" />}
          onClick={applySwitch}
          sx={{
            flexShrink: 0,
            ml: { xs: 0, sm: 0.5 },
            width: { xs: "100%", sm: "auto" },
          }}
        >
          Switch
        </Button>
      ) : null}
    </Box>
  );
}
